import React from 'react';
import PropTypes from 'prop-types';

// The Lockfiles step of a Console delivery (vfs-service src/publish/deliveryLockfiles.js): before the code is packed,
// every delivered service's lockfile is checked against its manifest and made or regenerated in the sandbox when
// missing or out of sync, then committed. `lockfilesSummary` is the step's one line; `PublishLockfilesFailure` lists
// what stopped the delivery, with the tool's last lines and how to fix it.

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** The step's one line: "Checked 2 services, none needed", "Updated 1 service: app", or null before it ran. */
export function lockfilesSummary(lockfiles) {
  if (!lockfiles || lockfiles.state === 'unavailable') { return null; }
  const services = lockfiles.services || [];
  const changed = services.filter((s) => s.status === 'created' || s.status === 'updated');
  if (lockfiles.state === 'failed') {
    const failed = services.filter((s) => s.blocking);
    return `Failed for ${failed.map((s) => s.service).join(', ') || 'a service'}`;
  }
  if (changed.length) { return `Updated ${plural(changed.length, 'service')}: ${changed.map((s) => s.service).join(', ')} (committed)`; }
  const checked = services.filter((s) => s.status !== 'skipped').length;
  return checked ? `Checked ${plural(checked, 'service')}, none needed` : 'None needed';
}

/**
 * The step's line while the sandbox checks them (the job's progress): "Checking api (1 of 3)", with ": waiting for
 * running checks" while Quality checks hold the sandbox's runner (the job waits up to 5 minutes per service), or null.
 */
export function lockfilesProgressLine(job) {
  if (!job || job.state !== 'running' || !job.current) { return null; }
  const line = job.total > 1 ? `Checking ${job.current} (${Math.min((job.done || 0) + 1, job.total)} of ${job.total})` : `Checking ${job.current}`;
  return job.waiting ? `${line}: waiting for running checks` : line;
}

/** What stopped the delivery at its Lockfiles step: per service the reason, the tool's last lines and a fix. */
export function PublishLockfilesFailure({ lockfiles }) {
  const failed = (lockfiles?.services || []).filter((s) => s.blocking);
  if (!failed.length) { return null; }
  return (
    <div className="publish-lockfiles" data-testid="publish-lockfiles-failed">
      {failed.map((s) => (
        <div className="publish-lockfiles-item" key={s.service}>
          <b>{s.service}</b>{s.file ? <> · <code>{s.file.replace(/^services\/[^/]+\//, '')}</code></> : null}
          <p className="publish-note">{s.message}{s.reason ? ` (${s.reason})` : ''}</p>
          {s.output && <pre className="publish-lockfiles-output">{s.output}</pre>}
          {s.fix && <p className="publish-note"><b>Fix:</b> {s.fix}</p>}
        </div>
      ))}
    </div>
  );
}

PublishLockfilesFailure.propTypes = {
  lockfiles: PropTypes.shape({ services: PropTypes.arrayOf(PropTypes.object) }),
};
